/* eslint-disable react-hooks/exhaustive-deps */
import { Table, DatePicker, Space, Button, Card } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { exportCSV } from "../../../utils/csvExport";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/ListPages.css";
const { RangePicker } = DatePicker;

const Reports = () => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    GetApiOrder();
  }, [startDate, endDate]);
  const GetApiOrder = async () => {
    try {
      // Create params object for the query parameters
      const params = {};
      if (startDate)
        params.start_date = dayjs(startDate).format("YYYY-MM-DD");
      if (endDate)
        params.end_date = dayjs(endDate).format("YYYY-MM-DD");

      // Use apiClient directly with params in config object
      const res = await apiClient.get("/reports/orders", { params });

      setDataSource(
        res.data.map((item) => ({
          id: item.id,
          customer: item.customer,
          total_price: item.total_price,
          amount_paid: item.amount_paid,
          productsTotal_price: item.total_price,
          sku: item.products.sku,
          outstanding_balance: item.outstanding_balance,
          payment_status: item.payment_status,
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const downloadCSV = () => {
    const csvColumns = [
      { key: "id", title: t("id") },
      { key: "customer", title: t("customer") },
      { key: "total_price", title: t("totalPrice") },
      { key: "amount_paid", title: t("amountPaid") },
      { key: "payment_status", title: t("paymentStatus") },
      { key: "outstanding_balance", title: t("outstandingBalance") },
    ];
    const filename = `order-report-${dayjs().format("YYYY-MM-DD")}`;
    exportCSV(csvColumns, dataSource, filename);
  };

  const downloadPDF = () => {
    const doc = new jsPDF();

    // Format the start and end dates
    const formattedStartDate = startDate
      ? dayjs(startDate).format("DD/MM/YYYY")
      : "N/A";
    const formattedEndDate = endDate
      ? dayjs(endDate).format("DD/MM/YYYY")
      : "N/A";

    // Add the report title and date range
    doc.text(t("Order Reports"), 14, 10);
    doc.text(
      `${t("Date Range")}:   ${formattedEndDate} to ${formattedStartDate}`,
      14,
      20
    );

    const tableColumn = [
      t("id"),
      t("customer"),
      t("Products Total Price"),
      t("amountPaid"),
      t("payment"),
      t("balance"),
    ];

    const tableRows = dataSource.map((item, index) => [
      index + 1, // Show the row index (ID)
      item.customer,
      // item.productsTotal_price, // Uncomment if you want to show this
      item.amount_paid,
      item.total_price,
      item.payment_status,
      item.outstanding_balance,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30, // Adjust the starting position to fit the date range
    });

    doc.save("Reports.pdf");
  };

  const columns = [
    {
      title: t("id"),
      key: "index",
      render: (text, record, index) => index + 1, // Display row index
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("customer")}</span>,

      dataIndex: "customer",
      key: "customer",
    }, {
      title: (
        <span style={{ fontWeight: "bold" }}>{t("outstandingBalance")}</span>
      ),
      dataIndex: "outstanding_balance",
      key: "outstanding_balance",
      render: (value) => formatCurrency(value),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("paymentStatus")}</span>,
      dataIndex: "payment_status",
      key: "payment_status",
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("amountPaid")}</span>,
      dataIndex: "amount_paid",
      key: "amount_paid",
      render: (value) => formatCurrency(value),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("totalPrice")}</span>,
      dataIndex: "total_price",
      key: "total_price",
      render: (value) => formatCurrency(value),
    },
  ];

  const filteredData = useMemo(
    () =>
      filterBySearch(dataSource, searchText, (item) => [
        item.id,
        item.customer,
        item.sku,
        item.total_price,
        item.amount_paid,
        item.outstanding_balance,
        item.payment_status,
      ]),
    [dataSource, searchText]
  );

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("reports")}
          </span>
        }
        variant="borderless"
      >
        <Space
          style={{
            marginBottom: 16,
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <ListSearch value={searchText} onChange={setSearchText} width={320} />
          <RangePicker
            onChange={(dates) => {
              setStartDate(dates ? dates[0] : null);
              setEndDate(dates ? dates[1] : null);
            }}
            style={{ minWidth: 240 }}
          />
          <Button type="primary" onClick={downloadPDF}>
            Download PDF
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={downloadCSV}>
            CSV
          </Button>
        </Space>

        <Table
          dataSource={filteredData.map((item) => ({ ...item, key: item.id }))}
          columns={columns}
          pagination={{ pageSize: 50 }}
          scroll={{ x: 600 }}
        />
      </Card>
    </div>
  );
};

export default Reports;

